import { Injectable } from '@angular/core';
import {SignalRHttpClient} from '../../core/services/signal-r-http-client';
import {EMPTY, Observable} from 'rxjs';
import {config} from '../config';
import {NotificationsWrapper} from '../models/notifications-wrapper';
import {map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {UpdateNotification} from '../models/update-notification';
import {Notification} from '../models/notification';
import {NotificationWrapper} from '../models/notification-wrapper';
import {NotificationStats} from '../models/notification-stats';
import {NotificationFilter} from '../models/notification-filter';
import {TranslateService} from '../../core/translate/translate.service';
import {HubService} from '../../core/services/hub.service';
import { AuthFacade } from '../../stores/auth/facades/auth.facade';
import { CoreFacade } from '../../stores/core/facades/core.facade';
import { ModalService } from '../../core/services/modal.service';
import { SocketsManagerService } from '../../sockets-manager/services';
import { Store } from '@ngrx/store';
import * as ReportWorkspaceActions from '../../stores/reports/actions/report-workspace.actions';

@Injectable({
  providedIn: 'root'
})
export class NotificationService extends HubService {
  private page = 1;
  // The drawer is full height, so ten rows left it looking empty and made every scroll a
  // round trip. Thirty fills it and still keeps the payload small.
  private pageSize = 30;
  private totalPages: number;

  constructor(
    protected signalRHttpClient: SignalRHttpClient,
    protected modalService: ModalService,
    protected translate: TranslateService,
    protected authFacade: AuthFacade,
    private http: HttpClient,
    protected socketsManagerService: SocketsManagerService,
    private coreFacade: CoreFacade,
    private store: Store<any>
  ) {
    super('hubs/notification', modalService, translate, signalRHttpClient, authFacade, socketsManagerService, coreFacade);
    const subscription = this.coreFacade.getNotificationStats$.subscribe(stats => {
      if (stats) {
        this.page = stats.currentPage;
        this.totalPages = stats.totalPages;
      }
    });
    this.subscriptions.add(subscription);
  }

  getNotifications(page, filter?: NotificationFilter): Observable<NotificationsWrapper> {
    const fields = ['*'];
    const url = config.generateApiUrl('notifications', fields, null, 2.2)
      + `&page=${page}&pageSize=${this.pageSize}`
      + NotificationService.toQuery(filter);

    return this.http.get(url).pipe(
      map(request => {
        return request['data'] as NotificationsWrapper;
      })
    );
  }

  /**
   * The filters the API understands, as query parameters.
   *
   * Filtering is done by the server so that it covers the user's whole history. Doing it in the
   * browser could only ever narrow the pages already fetched, which is misleading for an account
   * holding thousands of notifications.
   */
  private static toQuery(filter?: NotificationFilter): string {
    if (!filter) {
      return '';
    }

    const parts: string[] = [];
    if (filter.status) { parts.push(`status=${encodeURIComponent(filter.status)}`); }
    if (filter.severity) { parts.push(`severity=${encodeURIComponent(filter.severity)}`); }
    if (filter.from) { parts.push(`from=${NotificationService.toUtcParam(filter.from, 'start')}`); }
    if (filter.to) { parts.push(`to=${NotificationService.toUtcParam(filter.to, 'end')}`); }
    if (filter.search?.trim()) { parts.push(`search=${encodeURIComponent(filter.search.trim())}`); }

    return parts.length ? `&${parts.join('&')}` : '';
  }

  /**
   * A picked day, as the UTC instant that day begins or ends at for this browser.
   *
   * Notifications are stored in UTC and the API compares in UTC, so the conversion has to happen
   * here: only the browser knows which absolute moments the user meant by "the 21st". In Warsaw that
   * day runs from 21st 00:00+02:00 to 21st 23:59:59+02:00, which is 20th 22:00Z to 21st 21:59:59Z.
   */
  private static toUtcParam(value: Date, edge: 'start' | 'end'): string {
    const local = new Date(value);
    if (edge === 'start') {
      local.setHours(0, 0, 0, 0);
    } else {
      local.setHours(23, 59, 59, 999);
    }

    return encodeURIComponent(local.toISOString());
  }

  /**
   * Version 2.1, not 2.2: the 2.2 endpoint binds only page and pageSize, so it silently ignores
   * notificationIds and answers with the newest notifications instead of the ones asked for. That
   * happened to look right, because a notification that has just arrived is usually also the newest.
   */
  getNotificationByIds(ids: number[]): Observable<NotificationsWrapper> {
    // Length, not hasValue: an empty array is not null, so hasValue lets it through and the request
    // goes out with pageSize=0.
    if (!ids?.length) {
      return EMPTY;
    }
    const fields = ['*'];

    return this.http.get(config.generateApiUrl('notifications', fields, null, 2.1) + `&page=1&pageSize=${ids.length}&notificationIds=${ids.join(',')}`).pipe(
      map(request => {
        return request['data'] as NotificationsWrapper;
      })
    );
  }

  updateNotificationStatus(notificationToUpdate: UpdateNotification) {
    const fields = ['*'];
     return this.http.put(config.generateApiUrl('notifications', fields, null, 2.1), notificationToUpdate)
      .pipe(map(res => res['data']['item']));
  }

  /**
   * Marks every unread notification of this user, server side.
   *
   * The id based update cannot do this: a user with thousands unread would have to page through all
   * of them first just to collect the ids.
   */
  markAllAsRead(): Observable<number> {
    const fields = ['*'];
    return this.http.put(config.generateApiUrl('notifications/read-all', fields, null, 2.1), {})
      .pipe(map(res => res['data']?.['item'] ?? 0));
  }

  update(notifications: Notification[]) {
    const list = notifications.map(n => ({
      ...n,
      date: this.utils.dateToLocal(n.date).toString(),
      data: NotificationService.parseData(n.data),
    }));
    return this.sortNotifications(list);
  }

  /**
   * The payload as an object where it is JSON, and as it arrived where it is not.
   *
   * Share-link notifications were stored with a bare id as their payload. JSON.parse threw on it,
   * which failed the whole page: "load more" fetched the next page and showed nothing, and a day
   * holding one of them never loaded at all.
   */
  private static parseData(data: unknown): unknown {
    if (typeof data !== 'string') {
      return data;
    }

    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }

  sortNotifications(notifications: Notification[]) {
    return notifications.sort(function(a, b) {
      const dateA: number = new Date(a.date).getTime();
      const dateB: number = new Date(b.date).getTime();
      return dateB - dateA;
    });
  }

  protected onConnection() {
    this.coreFacade.getNotifications(1);
  }

  protected onInit() {
    this.hubConnection.on('Notification', (data: NotificationWrapper) => {
      this.coreFacade.getNotificationByIds(data.notificationIds);
      this.coreFacade.setNotificationsStats(new NotificationStats(data.unreadNotificationsCount, this.page, this.totalPages));
    });
    this.hubConnection.on('UpdateNotification', (data: NotificationWrapper) => {
      // No ids means "many changed at once", which is how marking everything read arrives. Naming
      // them all would ask for the whole history back in one request, so reload the first page
      // instead — that also refreshes the list in the tab that did not press the button.
      if (data.notificationIds?.length) {
        this.coreFacade.getNotificationByIds(data.notificationIds);
      } else {
        this.coreFacade.getNotifications(1);
      }
      this.coreFacade.setNotificationsStats(new NotificationStats(data.unreadNotificationsCount, this.page, this.totalPages));
    });
    this.hubConnection.on('ReportStatusChanged', (data: any) => {
      if (data?.generationAttemptId) {
        this.store.dispatch(ReportWorkspaceActions.refreshReportRequest({
          generationAttemptId: data.generationAttemptId,
        }));
      }
    });
  }

}
